import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { FiDownload, FiFilter, FiSearch, FiXCircle } from "react-icons/fi";

const EmployeeDashboard = () => {
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  const navigate = useNavigate();

  // 🔒 Check token on mount
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/employeeLogin");
    }
  }, [navigate]);

  useEffect(() => {
    fetchApplications();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [applications, searchTerm]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const employeeId = localStorage.getItem("employeeId");

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/employee/${employeeId}/applicants`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();
      if (response.ok) {
        const enrichedData = data.data.map((app) => ({
          ...app,
          remark: "",
          status: "",
          customLocation: "",
        }));
        setApplications(enrichedData);
        setFilteredApplications(enrichedData);
      } else {
        console.error("Failed to fetch applications", data.message);
      }
    } catch (err) {
      console.error("Error fetching applications:", err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let result = [...applications];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (app) =>
          `${app.firstName} ${app.lastName}`.toLowerCase().includes(term) ||
          app.jobTitle?.toLowerCase().includes(term) ||
          app.email?.toLowerCase().includes(term)
      );
    }
    setFilteredApplications(result);
    setCurrentPage(1);
  };

  const handleViewResume = (application) => {
    if (!application.resume) {
      alert("No resume available for this application");
      return;
    }

    let key = application.resume;
    if (key.startsWith("http")) {
      const url = new URL(key);
      key = url.pathname.substring(1);
    }

    if (!key.startsWith("Resume/")) {
      key = `Resume/${key}`;
    }

    const encodedKey = encodeURIComponent(key);
    navigate(`/view-resume/${encodedKey}`);
  };

  const handleChange = (id, field, value) => {
    setApplications((prev) =>
      prev.map((app) => (app._id === id ? { ...app, [field]: value } : app))
    );
  };

  const handleSave = async (application) => {
    try {
      const token = localStorage.getItem("token");

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/employee/updateApplicant/${application._id}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            applicationId: application._id,
            remark: application.remark,
            status: application.status,
            customLocation: application.customLocation,
          }),
        }
      );

      const result = await response.json();

      if (response.ok) {
        alert("Application updated successfully!");
      } else {
        alert("Failed to update application: " + result.message);
      }
    } catch (error) {
      console.error("Error updating application:", error);
      alert("An error occurred while saving.");
    }
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/employee/logout`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        localStorage.removeItem("token");
        localStorage.removeItem("employeeId");

        // Replace current history entry to prevent back navigation
        window.location.replace("/employeeLogin");
      } else {
        alert("Logout failed");
      }
    } catch (error) {
      console.error("Logout error:", error);
      alert("An error occurred during logout.");
    }
  };

  const totalPages = Math.ceil(filteredApplications.length / itemsPerPage);
  const currentItems = filteredApplications.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading applications...</p>
      </div>
    );
  }

  return (
    <div className="applications-container">
      <div
        className="logout-button"
        style={{ position: "absolute", top: "15px", right: "15px", zIndex: 10 }}
      >
        <button
          onClick={handleLogout}
          style={{
            padding: "6px 12px",
            backgroundColor: "#e53935",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Logout
        </button>
      </div>

      <div className="applications-header">
        <h2 className="applications-heading">My Assigned Candidates</h2>
      </div>

      <div className="search-filter-container">
        <div className="search-box">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search candidates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <FiXCircle
              className="clear-search"
              onClick={() => setSearchTerm("")}
            />
          )}
        </div>
      </div>

      {filteredApplications.length > 0 ? (
        <div className="table-container">
          <table className="applications-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Job Title</th>
                <th>Applicant Name</th>
                <th>Email</th>
                <th>Contact No</th>
                <th>Location</th>
                <th>City</th>
                <th>Notice Period</th>
                <th>Current CTC</th>
                <th>Expected CTC</th>
                <th>Referral Email</th>
                <th>Applied On</th>
                <th>Resume</th>
                <th>Custom Location</th>
                <th>Remark</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map((application, index) => (
                <tr key={application._id}>
                  <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                  <td>
                    {application.jobTitle || application.jobId?.title || "N/A"}
                  </td>
                  <td>{`${application.firstName} ${application.lastName}`}</td>
                  <td>{application.email}</td>
                  <td>{application.phone}</td>
                  <td>{application.location?.permanentAddress || "N/A"}</td>
                  <td>{application.location?.city || "N/A"}</td>
                  <td>{application.noticePeriod || "N/A"}</td>
                  <td>{application.currentCTC || "N/A"}</td>
                  <td>{application.expectedCTC || "N/A"}</td>
                  <td>{application.referralEmail || "N/A"}</td>
                  <td>
                    {application.createdAt
                      ? format(new Date(application.createdAt), "MMM dd, yyyy")
                      : "N/A"}
                  </td>
                  <td>
                    <button onClick={() => handleViewResume(application)}>
                      View Resume
                    </button>
                  </td>
                  <td>
                    <input
                      type="text"
                      value={application.customLocation}
                      onChange={(e) =>
                        handleChange(
                          application._id,
                          "customLocation",
                          e.target.value
                        )
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={application.remark}
                      onChange={(e) =>
                        handleChange(application._id, "remark", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <select
                      value={application.status}
                      onChange={(e) =>
                        handleChange(application._id, "status", e.target.value)
                      }
                    >
                      <option value="">Select</option>
                      <option value="Shortlisted">Shortlisted</option>
                      <option value="Rejected">Rejected</option>
                      <option value="Hold">Hold</option>
                    </select>
                  </td>
                  <td>
                    <button
                      onClick={() => handleSave(application)}
                      style={{
                        padding: "5px 10px",
                        backgroundColor: "#4CAF50",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                      }}
                    >
                      Save
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="no-applications">
          <div className="empty-state-icon">📭</div>
          <p>No candidates found</p>
        </div>
      )}

      {filteredApplications.length > itemsPerPage && (
        <div className="pagination-container">
          <button
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span>
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default EmployeeDashboard;
